import { useState, useMemo, Fragment, useCallback } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
  RotateCcw,
  Minus,
  Check,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { DataTablePagination } from "./DataTablePagination";
import { DataTableActions } from "./DataTableActions";
import type {
  DataTableProps,
  ColumnBreakpoint,
  FilterDef,
  SelectFilterDef,
  CheckboxFilterDef,
  SortDirection,
} from "./types";

const breakpointClasses: Record<ColumnBreakpoint, string> = {
  always: "",
  sm: "hidden sm:table-cell",
  md: "hidden md:table-cell",
  lg: "hidden lg:table-cell",
  xl: "hidden xl:table-cell",
};

const alignClasses: Record<string, string> = {
  left: "text-left",
  right: "text-right",
  center: "text-center",
};

function isSelectFilter(f: FilterDef): f is SelectFilterDef {
  return f.type === "select";
}

function isCheckboxFilter(f: FilterDef): f is CheckboxFilterDef {
  return f.type === "checkbox";
}

function compareSortValues(
  a: string | number | boolean | Date | null,
  b: string | number | boolean | Date | null,
  direction: SortDirection
): number {
  // Nulls always last
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  let cmp = 0;
  if (a instanceof Date && b instanceof Date) {
    cmp = a.getTime() - b.getTime();
  } else if (typeof a === "number" && typeof b === "number") {
    cmp = a - b;
  } else if (typeof a === "boolean" && typeof b === "boolean") {
    cmp = (a ? 1 : 0) - (b ? 1 : 0);
  } else {
    cmp = String(a).localeCompare(String(b), "sv-SE");
  }

  return direction === "asc" ? cmp : -cmp;
}

export function DataTable<TData>({
  columns,
  data,
  rowKey,
  isLoading = false,
  emptyMessage = "Inga resultat hittades",
  search,
  onSearchChange,
  searchPlaceholder = "Sök...",
  filters,
  pagination,
  onPageChange,
  onRowClick,
  rowActions,
  expandedContent,
  rowClassName,
  serverSortColumnId,
  serverSortDirection,
  onSort,
  toolbarExtra,
  pageSizeOptions,
  onPageSizeChange,
  onResetFilters,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  bulkActions,
  canSelect = () => true,
}: DataTableProps<TData>) {
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [localSortColumnId, setLocalSortColumnId] = useState<string | null>(null);
  const [localSortDirection, setLocalSortDirection] = useState<SortDirection>("asc");

  // Controlled vs uncontrolled sort
  const isControlledSort = !!onSort;
  const sortColumnId = isControlledSort ? (serverSortColumnId ?? null) : localSortColumnId;
  const sortDirection = isControlledSort ? (serverSortDirection ?? "asc") : localSortDirection;

  const hasToolbar =
    search !== undefined || (filters && filters.length > 0) || toolbarExtra;
  const hasActions = rowActions && rowActions.length > 0;
  const hasExpander = !!expandedContent;
  const hasColumnFilters = columns.some((col) => col.filter);
  const hasActiveColumnFilters = columns.some((col) => {
    if (!col.filter) return false;
    return col.filter.value !== "";
  });
  const hasSelection = selectable && selectedKeys && onSelectionChange;
  const totalColumns =
    columns.length +
    (hasActions ? 1 : 0) +
    (hasExpander && !hasActions ? 1 : 0) +
    (hasSelection ? 1 : 0);

  // Sort data client-side (skip when server-side sorting is active)
  // NOTE: Must be defined before selection hooks that depend on it
  const sortedData = useMemo(() => {
    if (isControlledSort) return data;
    if (!sortColumnId) return data;
    const col = columns.find((c) => c.id === sortColumnId);
    if (!col?.sortAccessor) return data;

    const accessor = col.sortAccessor;
    return [...data].sort((a, b) =>
      compareSortValues(accessor(a), accessor(b), sortDirection)
    );
  }, [data, columns, sortColumnId, sortDirection, isControlledSort]);

  // Selection logic
  const selectableRows = useMemo(() => {
    if (!hasSelection) return [];
    return sortedData.filter(canSelect);
  }, [sortedData, hasSelection, canSelect]);

  const selectableKeys = useMemo(() => {
    return new Set(selectableRows.map(rowKey));
  }, [selectableRows, rowKey]);

  const selectedCount = selectedKeys?.size ?? 0;
  const selectableCount = selectableKeys.size;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && selectedCount < selectableCount;

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableKeys));
    }
  }, [allSelected, selectableKeys, onSelectionChange]);

  const handleSelectRow = useCallback(
    (key: string, selected: boolean) => {
      if (!onSelectionChange || !selectedKeys) return;
      const newSelection = new Set(selectedKeys);
      if (selected) {
        newSelection.add(key);
      } else {
        newSelection.delete(key);
      }
      onSelectionChange(newSelection);
    },
    [selectedKeys, onSelectionChange]
  );

  // Get selected row data for bulk actions
  const selectedRows = useMemo(() => {
    if (!selectedKeys || selectedKeys.size === 0) return [];
    return sortedData.filter((row) => selectedKeys.has(rowKey(row)));
  }, [sortedData, selectedKeys, rowKey]);

  const handleSort = (colId: string) => {
    if (isControlledSort) {
      // Controlled mode — delegate to parent via onSort callback
      if (sortColumnId === colId) {
        if (sortDirection === "asc") {
          onSort!(colId, "desc");
        } else {
          onSort!(colId, null);
        }
      } else {
        onSort!(colId, "asc");
      }
    } else {
      // Uncontrolled mode — local state
      if (sortColumnId === colId) {
        if (localSortDirection === "asc") {
          setLocalSortDirection("desc");
        } else {
          setLocalSortColumnId(null);
          setLocalSortDirection("asc");
        }
      } else {
        setLocalSortColumnId(colId);
        setLocalSortDirection("asc");
      }
    }
  };

  const handleRowClick = (row: TData) => {
    if (onRowClick) {
      onRowClick(row);
      return;
    }
    if (!expandedContent) return;
    const content = expandedContent(row);
    if (content === null) return;

    const key = rowKey(row);
    setExpandedRowKey((prev) => (prev === key ? null : key));
  };

  const isRowExpandable = (row: TData): boolean => {
    if (!expandedContent) return false;
    return expandedContent(row) !== null;
  };

  return (
    <div className="space-y-4">
      {/* Toolbar (for global search, toolbar-level filters, extras) */}
      {hasToolbar && (
        <div className="flex flex-col sm:flex-row gap-3">
          {search !== undefined && onSearchChange && (
            <div className="relative flex-1">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder:text-gray-400"
              />
            </div>
          )}

          {filters?.map((filter) => {
            if (isSelectFilter(filter)) {
              return (
                <select
                  key={filter.key}
                  value={filter.value}
                  onChange={(e) => filter.onChange(e.target.value)}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-gray-900"
                >
                  {filter.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              );
            }

            if (isCheckboxFilter(filter)) {
              return (
                <label
                  key={filter.key}
                  className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={filter.value}
                    onChange={(e) => filter.onChange(e.target.checked)}
                    className="rounded border-gray-200"
                  />
                  {filter.label}
                </label>
              );
            }

            return null;
          })}

          {toolbarExtra}
        </div>
      )}

      {/* Bulk Action Toolbar */}
      {hasSelection && selectedCount > 0 && bulkActions && bulkActions.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
          <span className="text-sm font-medium text-primary-700">
            {selectedCount} {selectedCount === 1 ? "markerad" : "markerade"}
          </span>
          <div className="h-4 w-px bg-primary-200" />
          <div className="flex items-center gap-2">
            {bulkActions.map((action) => (
              <Button
                key={action.key}
                variant={action.variant === "danger" ? "destructive" : action.variant === "success" ? "outline" : "outline"}
                size="sm"
                onClick={() => action.onClick(selectedRows)}
                disabled={action.loading}
                className={action.variant === "success" ? "text-green-600 border-green-300 hover:bg-green-50" : ""}
              >
                {action.loading ? (
                  <Spinner size="sm" />
                ) : (
                  action.icon && <action.icon size={14} />
                )}
                {action.label}
              </Button>
            ))}
          </div>
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => onSelectionChange?.(new Set())}
              className="text-xs text-primary-600 hover:text-primary-800 hover:underline"
            >
              Avmarkera alla
            </button>
          </div>
        </div>
      )}

      {/* Table Card */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {/* Header row */}
              <tr className="border-b border-gray-200 bg-gray-50">
                {/* Selection checkbox column */}
                {hasSelection && (
                  <th className="w-10 px-4 py-3">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectAll();
                      }}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        allSelected
                          ? "bg-primary-500 border-primary-500 text-white"
                          : someSelected
                            ? "bg-primary-100 border-primary-500 text-primary-500"
                            : "bg-white border-gray-200 hover:border-muted-foreground"
                      }`}
                      aria-label={allSelected ? "Avmarkera alla" : "Markera alla"}
                    >
                      {allSelected && <Check size={12} strokeWidth={3} />}
                      {someSelected && <Minus size={12} strokeWidth={3} />}
                    </button>
                  </th>
                )}
                {columns.map((col) => {
                  const isSortable = col.sortable && col.sortAccessor;
                  const isActive = sortColumnId === col.id;

                  return (
                    <th
                      key={col.id}
                      className={`px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider ${
                        alignClasses[col.align || "left"]
                      } ${breakpointClasses[col.hideBelow || "always"]} ${
                        col.className || ""
                      } ${isSortable ? "cursor-pointer select-none hover:text-gray-900 transition-colors" : ""}`}
                      onClick={
                        isSortable ? () => handleSort(col.id) : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.header}
                        {isSortable && (
                          <span className="inline-flex">
                            {isActive ? (
                              sortDirection === "asc" ? (
                                <ArrowUp size={13} className="text-gray-900" />
                              ) : (
                                <ArrowDown
                                  size={13}
                                  className="text-gray-900"
                                />
                              )
                            ) : (
                              <ArrowUpDown
                                size={13}
                                className="text-gray-400/50"
                              />
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
                {(hasActions || hasExpander) && (
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">
                    {hasActions ? "Åtgärder" : ""}
                  </th>
                )}
              </tr>

              {/* Column filter row */}
              {hasColumnFilters && (
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  {/* Empty cell for selection column */}
                  {hasSelection && <td className="w-10 px-4 py-2" />}
                  {columns.map((col) => {
                    const filter = col.filter;
                    return (
                      <td
                        key={`filter-${col.id}`}
                        className={`px-4 py-2 ${
                          breakpointClasses[col.hideBelow || "always"]
                        }`}
                      >
                        {filter?.type === "text" && (
                          <div className="relative">
                            <input
                              type="text"
                              value={filter.value}
                              onChange={(e) => filter.onChange(e.target.value)}
                              placeholder={filter.placeholder || ""}
                              onClick={(e) => e.stopPropagation()}
                              className={`w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-gray-400 ${filter.value ? "pr-7" : ""}`}
                            />
                            {filter.value && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); filter.onChange(""); }}
                                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
                                aria-label="Rensa"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        )}
                        {filter?.type === "select" && (
                          <div className="relative">
                            <select
                              value={filter.value}
                              onChange={(e) => filter.onChange(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className={`w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 ${filter.value ? "pr-7" : ""}`}
                            >
                              {filter.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            {filter.value && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); filter.onChange(""); }}
                                className="absolute right-5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-900 transition-colors"
                                aria-label="Rensa"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        )}
                        {filter?.type === "date" && (
                          <div className="relative">
                            <input
                              type="date"
                              value={filter.value}
                              onChange={(e) => filter.onChange(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                            />
                            {filter.value && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); filter.onChange(""); }}
                                className="absolute right-6 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-900 transition-colors bg-white"
                                aria-label="Rensa"
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {(hasActions || hasExpander) && (
                    <td className="px-4 py-2 text-right">
                      {onResetFilters && hasActiveColumnFilters && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onResetFilters(); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-400 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                          title="Rensa alla filter"
                        >
                          <RotateCcw size={12} />
                          <span className="hidden lg:inline">Rensa</span>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={totalColumns} className="py-16">
                    <div className="flex items-center justify-center">
                      <Spinner size="lg" />
                    </div>
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalColumns}
                    className="text-center py-16 text-gray-400"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                sortedData.map((row) => {
                  const key = rowKey(row);
                  const isExpanded = expandedRowKey === key;
                  const expandable = isRowExpandable(row);
                  const isSelectable = hasSelection && canSelect(row);
                  const isSelected = hasSelection && selectedKeys?.has(key);

                  return (
                    <Fragment key={key}>
                      <tr
                        className={`hover:bg-gray-50/50 transition-colors ${
                          onRowClick || (expandedContent && expandable)
                            ? "cursor-pointer"
                            : ""
                        } ${isSelected ? "bg-primary-50/50" : ""} ${rowClassName?.(row) || ""}`}
                        onClick={() => handleRowClick(row)}
                      >
                        {/* Selection checkbox cell */}
                        {hasSelection && (
                          <td className="w-10 px-4 py-3">
                            {isSelectable ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(key, !isSelected);
                                }}
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? "bg-primary-500 border-primary-500 text-white"
                                    : "bg-white border-gray-200 hover:border-muted-foreground"
                                }`}
                                aria-label={isSelected ? "Avmarkera" : "Markera"}
                              >
                                {isSelected && <Check size={12} strokeWidth={3} />}
                              </button>
                            ) : (
                              <div className="w-5 h-5" />
                            )}
                          </td>
                        )}
                        {columns.map((col) => (
                          <td
                            key={col.id}
                            className={`px-4 py-3 ${
                              alignClasses[col.align || "left"]
                            } ${
                              breakpointClasses[col.hideBelow || "always"]
                            } ${col.className || ""}`}
                          >
                            {col.cell(row)}
                          </td>
                        ))}
                        {hasActions && (
                          <td className="px-4 py-3 text-right">
                            <DataTableActions
                              row={row}
                              actions={rowActions!}
                            />
                          </td>
                        )}
                        {hasExpander && !hasActions && (
                          <td className="px-4 py-3 text-right">
                            {expandable && (
                              <div className="text-gray-400">
                                {isExpanded ? (
                                  <ChevronUp size={16} />
                                ) : (
                                  <ChevronDown size={16} />
                                )}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                      {isExpanded && expandedContent && (
                        <tr>
                          <td colSpan={totalColumns} className="px-4 pb-3">
                            {expandedContent(row)}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && onPageChange && (
          <DataTablePagination
            pagination={pagination}
            onPageChange={onPageChange}
            pageSizeOptions={pageSizeOptions}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </div>
    </div>
  );
}
